import { Link } from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { useTxnPanelRefresh } from "@app/components/txn-table-panel/use-txn-panel-refresh";
import CacTxnsDrawer from "@app/pages/cac/cac-txns-drawer";

import type { cacMonthPanel_Query } from "@app/queries/cacMonthPanel_Query.graphql";

const monthQuery = graphql`
  query cacMonthPanel_Query($month: String!) {
    cacMonthBreakdown(month: $month) {
      month
      components {
        key
        label
        totalSpend
        subcategories {
          key
          label
          totalSpend
          transactions {
            postingDate
            description
            spend
            txn {
              id
            }
            counterparty {
              name
            }
            category {
              name
            }
          }
        }
      }
    }
  }
`;

type Component =
  cacMonthPanel_Query["response"]["cacMonthBreakdown"]["components"][number];
type Subcategory = Component["subcategories"][number];
type Txn = Subcategory["transactions"][number];

const money0 = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
const money2 = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });

function monthLabel(month: string): string {
  const date = new Date(`${month}-01T00:00:00`);
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

// A subcategory drill opened on the shared editable shell: the title shown in the
// drawer header and the explicit set of leaf txn ids to load.
type Drill = { title: string; txnIds: string[] };

export default function CacMonthPanel({
  month,
  includedKeys,
  colorByKey,
  onClose,
}: {
  month: string;
  includedKeys: readonly string[];
  colorByKey: Record<string, string>;
  onClose: () => void;
}) {
  // The cost-centre tree is the read-only navigator; clicking "Edit N" on a
  // subcategory opens its leaf txns on the editable shell. Edits re-derive
  // server-side, so the shared refresh seam re-fetches the breakdown to reconcile
  // spend — the fetch key lives up here because the drawer that triggers the edit
  // is a sibling of the body that reads it.
  const [drill, setDrill] = useState<Drill | null>(null);
  const { refreshing, refresh, queryOptions } = useTxnPanelRefresh();

  return (
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-xl bg-white shadow-xl flex flex-col">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h2 className="font-semibold">
            Acquisition spend —{" "}
            <Link
              to="/transactions/$month"
              params={{ month }}
              className="text-blue-600 hover:underline"
            >
              {monthLabel(month)}
            </Link>
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-900 text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div
          className={`flex-1 overflow-y-auto px-5 py-4 ${refreshing ? "opacity-50 transition-opacity" : ""}`}
          aria-busy={refreshing}
        >
          <Suspense
            fallback={<p className="text-gray-500">Loading transactions…</p>}
          >
            <PanelBody
              month={month}
              includedKeys={includedKeys}
              colorByKey={colorByKey}
              queryOptions={queryOptions}
              onOpenDrill={setDrill}
            />
          </Suspense>
        </div>
      </div>

      {drill && (
        <CacTxnsDrawer
          title={drill.title}
          txnIds={drill.txnIds}
          onClose={() => setDrill(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}

function PanelBody({
  month,
  includedKeys,
  colorByKey,
  queryOptions,
  onOpenDrill,
}: {
  month: string;
  includedKeys: readonly string[];
  colorByKey: Record<string, string>;
  queryOptions: ReturnType<typeof useTxnPanelRefresh>["queryOptions"];
  onOpenDrill: (drill: Drill) => void;
}) {
  const data = useLazyLoadQuery<cacMonthPanel_Query>(monthQuery, { month }, queryOptions);
  const included = new Set(includedKeys);
  // Only the cost centres currently selected on the page. The backend already
  // orders components by spend (largest first); filtering preserves that order.
  const components = data.cacMonthBreakdown.components.filter((c) =>
    included.has(c.key)
  );
  const grandTotal = components.reduce((sum, c) => sum + c.totalSpend, 0);

  if (components.length === 0) {
    return (
      <p className="text-gray-500">
        No cost centres selected, or no acquisition spend this month.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between border-b pb-2">
        <span className="text-sm text-gray-500">
          Acquisition spend — selected cost centres
        </span>
        <span className="text-xl font-bold tabular-nums">
          {money0(grandTotal)}
        </span>
      </div>

      <ul>
        {components.map((component) => (
          <CostCentreRow
            key={component.key}
            component={component}
            color={colorByKey[component.key]}
            onOpenDrill={onOpenDrill}
          />
        ))}
      </ul>
    </div>
  );
}

function CostCentreRow({
  component,
  color,
  onOpenDrill,
}: {
  component: Component;
  color?: string;
  onOpenDrill: (drill: Drill) => void;
}) {
  const [open, setOpen] = useState(false);
  // A cost centre whose only group is its own "self" bucket (spend booked
  // straight on its node, no child subcategories) expands directly to its
  // items — no redundant single-subcategory level in between.
  const selfOnly =
    component.subcategories.length === 1 &&
    component.subcategories[0].key === component.key;

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="w-full flex items-center gap-2 border-b py-2 text-left hover:bg-gray-50"
      >
        <Chevron open={open} />
        <span
          className="inline-block w-3 h-3 rounded-sm shrink-0"
          style={{ backgroundColor: color ?? "#9ca3af" }}
        />
        <span className="font-medium flex-1 truncate">{component.label}</span>
        <span className="tabular-nums font-medium">
          {money0(component.totalSpend)}
        </span>
      </button>

      {open &&
        (selfOnly ? (
          <TxnList
            title={component.label}
            transactions={component.subcategories[0].transactions}
            onOpenDrill={onOpenDrill}
          />
        ) : (
          <ul>
            {component.subcategories.map((sub) => (
              <SubcategoryRow
                key={sub.key}
                title={`${component.label} · ${sub.label}`}
                subcategory={sub}
                onOpenDrill={onOpenDrill}
              />
            ))}
          </ul>
        ))}
    </li>
  );
}

function SubcategoryRow({
  title,
  subcategory,
  onOpenDrill,
}: {
  title: string;
  subcategory: Subcategory;
  onOpenDrill: (drill: Drill) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="w-full flex items-center gap-2 border-b py-1.5 pl-7 text-left hover:bg-gray-50"
      >
        <Chevron open={open} />
        <span className="flex-1 truncate text-sm">{subcategory.label}</span>
        <span className="tabular-nums text-sm">
          {money0(subcategory.totalSpend)}
        </span>
      </button>
      {open && (
        <TxnList
          title={title}
          transactions={subcategory.transactions}
          onOpenDrill={onOpenDrill}
        />
      )}
    </li>
  );
}

// Items are ordered by spend (largest first) — date is irrelevant within a
// single month, so it rides along muted on the description line. An "Edit N"
// button opens this set on the shared editable shell (drill by explicit txn id).
function TxnList({
  title,
  transactions,
  onOpenDrill,
}: {
  title: string;
  transactions: readonly Txn[];
  onOpenDrill: (drill: Drill) => void;
}) {
  if (transactions.length === 0) {
    return (
      <p className="text-sm text-gray-400 py-1 pl-12">No categorized spend.</p>
    );
  }

  const txnIds = transactions
    .map((txn) => txn.txn?.id)
    .filter((id): id is string => Boolean(id));

  return (
    <>
      {txnIds.length > 0 && (
        <div className="pl-12 pr-2 py-1">
          <button
            type="button"
            onClick={() => onOpenDrill({ title, txnIds })}
            className="text-xs text-blue-600 hover:underline"
          >
            Edit {txnIds.length} transaction{txnIds.length === 1 ? "" : "s"}…
          </button>
        </div>
      )}
      <table className="w-full text-sm">
        <tbody>
          {transactions.map((txn, index) => (
          <tr key={index} className="border-b last:border-b-0 align-top">
            <td className="py-1.5 pl-12 pr-2">
              <span className="font-medium">
                {txn.counterparty?.name ?? "—"}
              </span>
              <span className="text-gray-400"> · {txn.category?.name ?? "—"}</span>
              <div
                className="text-gray-500 text-xs max-w-xs truncate"
                title={txn.description}
              >
                {txn.postingDate} · {txn.description}
              </div>
            </td>
            <td className="py-1.5 text-right whitespace-nowrap tabular-nums">
              {money2(txn.spend)}
            </td>
          </tr>
        ))}
        </tbody>
      </table>
    </>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 20 20"
      className={`w-3 h-3 shrink-0 text-gray-400 transition-transform ${
        open ? "rotate-90" : ""
      }`}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M7 5l6 5-6 5V5z" />
    </svg>
  );
}
